import React, { useCallback, useMemo } from 'react';

import { DndContext, UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { Control, Controller, useController, useFieldArray } from 'react-hook-form';
import { theme } from '~/theme';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import ArrayField from '../fields/ArrayField';
import ArrayFieldItem from '../fields/ArrayFieldItem';

import { ACCORDION_SECTION_IDS, useAccordionExpanded } from './AccordionExpandedContext';

interface Props {
  control: Control<any>;
  schema: any;
  setValue: (name: string, value: unknown, config?: object) => void;
}

const SECTION_ID = ACCORDION_SECTION_IDS.TAGS_AND_MARKETING;

const getArrayEnumOptions = (schema: any, fieldName: string): Array<string> =>
  schema?.properties?.[fieldName]?.items?.enum ?? [];

// Do not provide content team 'velocity' as an option to be set during offer edit.
// https://aussiecommerce.atlassian.net/browse/PP-1477
const getPartnershipCodeOptions = (schema: any): Array<string> =>
  (schema?.properties?.partnerships?.items?.properties?.code?.enum ?? []).filter((item) => item !== 'velocity');

function TagsAndMarketingAccordion({ control, schema, setValue }: Props) {
  const { isSectionExpanded, toggleSection } = useAccordionExpanded();

  const handleAccordionChange = useCallback(() => {
    toggleSection(SECTION_ID);
  }, [toggleSection]);

  const partnershipArray = useFieldArray({
    control,
    name: 'partnerships',
  });
  const {
    field: { value: partnershipsValue, onChange: onChangeArray },
    fieldState: { error: partnershipsError },
  } = useController({
    control,
    name: 'partnerships',
  });
  const partnerships = Array.isArray(partnershipsValue) ? partnershipsValue : [];

  const sortablePartnerships = useMemo<Array<UniqueIdentifier>>(
    () => partnershipArray.fields?.map((item, index) => `${item.id}-${index}`) ?? [],
    [partnershipArray.fields],
  );
  const locationOptions = getArrayEnumOptions(schema, 'locations');
  const holidayTypeOptions = getArrayEnumOptions(schema, 'holiday_types');
  const campaignOptions = getArrayEnumOptions(schema, 'campaigns');
  const partnershipCodeOptions = getPartnershipCodeOptions(schema);

  function handleDragEnd(event) {
    const { active, over } = event;
    if (over && active?.id !== over.id) {
      const oldIndex = sortablePartnerships.indexOf(active?.id);
      const newIndex = sortablePartnerships.indexOf(over?.id);
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const newArray = arrayMove(partnerships, oldIndex, newIndex);
      setValue('partnerships', newArray);
    }
  }

  const onPartnershipsChange = (e, name, index) => {
    const newArray = [...partnerships];
    const updatedValue = { ...newArray[index], [name]: e.target.value };
    newArray.splice(index, 1, updatedValue);
    setValue(e.target.name, e.target.value);
    onChangeArray(newArray);
  };

  return (
    <Accordion expanded={isSectionExpanded(SECTION_ID)} onChange={handleAccordionChange} disableGutters>
      <AccordionSummary
        aria-controls="tags-and-marketing"
        expandIcon={<ExpandMoreIcon />}
        id="tags-and-marketing"
        sx={{
          backgroundColor: theme.palette.grey[200],
        }}
      >
        <Typography variant="h5">Tags & Marketing</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack mt={2} spacing={2}>
          <ArrayField
            control={control}
            label="Location Tags"
            name="locations"
            render={(props) => (
              <Select {...props} fullWidth>
                {locationOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          <ArrayField
            control={control}
            label="Holiday Type Tags"
            name="holiday_types"
            render={(props) => (
              <Select {...props} fullWidth>
                {holidayTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          <ArrayField
            control={control}
            label="Campaign Tags"
            name="campaigns"
            render={(props) => (
              <Select {...props} fullWidth>
                {campaignOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          <Stack>
            <Typography variant="h6">Partnerships</Typography>
            <DndContext onDragEnd={handleDragEnd}>
              <SortableContext items={sortablePartnerships}>
                {partnershipArray.fields.map((item, index) => (
                  <FormControl
                    error={partnershipsError?.[index]}
                    key={item.id}
                    sx={{ display: 'flex', mb: 2, width: 1 }}
                  >
                    <ArrayFieldItem itemKey={item.id} index={index}>
                      <>
                        <Stack spacing={2} sx={{ width: 1 }}>
                          <Controller
                            render={({ field }) => (
                              <Select {...field} onChange={(e) => onPartnershipsChange(e, 'code', index)} fullWidth>
                                {partnershipCodeOptions.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                            name={`partnerships.${index}.code`}
                            control={control}
                          />
                          <Controller
                            render={({ field }) => (
                              <TextField
                                {...field}
                                onChange={(e) => onPartnershipsChange(e, 'upsell_text', index)}
                                fullWidth
                                label="Upsell text"
                                variant="outlined"
                              />
                            )}
                            name={`partnerships.${index}.upsell_text`}
                            control={control}
                          />
                        </Stack>
                        <Stack>
                          <IconButton onClick={() => partnershipArray.remove(index)} color="error">
                            <DeleteIcon fontSize="medium" />
                          </IconButton>
                        </Stack>
                      </>
                    </ArrayFieldItem>
                  </FormControl>
                ))}
                {!partnershipArray.fields.length && (
                  <Box
                    sx={{
                      p: 2,
                      width: 1,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="body2">Empty</Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    width: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <IconButton
                    onClick={() =>
                      partnershipArray.append({
                        code: '',
                        upsell_text: '',
                      })
                    }
                    size="large"
                    color="primary"
                  >
                    <AddIcon fontSize="large" />
                  </IconButton>
                </Box>
              </SortableContext>
            </DndContext>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default TagsAndMarketingAccordion;
